// src/components/data-display/DataTable.jsx

import { isValidElement, useMemo, useState } from 'react';

const isEmptySortValue = value => value == null || value === '';

const formatCellValue = value => {
  if (isValidElement(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ') || '-';
  }

  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const getColumnValue = (column, row) =>
  column.getValue ? column.getValue(row) : row?.[column.key];

const compareValues = (valueA, valueB) => {
  if (isEmptySortValue(valueA) && isEmptySortValue(valueB)) {
    return 0;
  }

  if (valueA instanceof Date && valueB instanceof Date) {
    return valueA.getTime() - valueB.getTime();
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }

  return String(valueA).localeCompare(String(valueB), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const resolveRowKey = (rowKey, row, index) => {
  if (typeof rowKey === 'function') {
    return rowKey(row, index);
  }

  return row?.[rowKey] ?? row?._id ?? row?.key ?? index;
};

const renderEmptyState = (emptyState, emptyMessage) => {
  if (typeof emptyState === 'function') {
    return emptyState({ message: emptyMessage });
  }

  if (emptyState) {
    return emptyState;
  }

  return (
    <p className="data-display-table__empty" style={{ fontSize: '12px' }}>
      {emptyMessage}
    </p>
  );
};

const containerStyle = maxHeight => ({
  maxHeight,
  overflow: maxHeight ? 'auto' : undefined,
  width: '100%',
});

const getTableStyle = minWidth => ({
  borderCollapse: 'collapse',
  fontSize: '14px',
  minWidth,
  width: '100%',
});

const getCellStyle = column => ({
  borderBottom: '1px solid #eee',
  padding: '4px',
  textAlign: column.align || 'left',
  width: column.width,
});

const getHeaderStyle = column => ({
  borderBottom: '1px solid #ccc',
  padding: '4px',
  position: column.sticky ? 'sticky' : undefined,
  textAlign: column.align || 'left',
  top: column.sticky ? 0 : undefined,
  width: column.width,
});

export default function DataTable({
  rows = [],
  columns = [],
  rowKey = 'id',
  caption,
  captionMode,
  density = 'standard',
  sortable = false,
  defaultSortKey = null,
  defaultSortDirection = 'asc',
  stickyHeader = false,
  maxHeight,
  minWidth,
  onRowActivate,
  emptyMessage = 'No data available',
  emptyState,
  className,
  showHeader = true,
  tableClassName,
  ariaLabelledby,
}) {
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultSortDirection,
  });

  const sortedRows = useMemo(() => {
    if (!sortable || !sortConfig.key) {
      return rows;
    }

    const column = columns.find(item => item.key === sortConfig.key);

    if (!column) {
      return rows;
    }

    const direction = sortConfig.direction === 'desc' ? -1 : 1;

    return [...rows].sort((rowA, rowB) => {
      const valueA = column.sortValue
        ? column.sortValue(rowA)
        : getColumnValue(column, rowA);
      const valueB = column.sortValue
        ? column.sortValue(rowB)
        : getColumnValue(column, rowB);

      if (isEmptySortValue(valueA)) {
        return isEmptySortValue(valueB) ? 0 : 1;
      }

      if (isEmptySortValue(valueB)) {
        return -1;
      }

      return compareValues(valueA, valueB) * direction;
    });
  }, [columns, rows, sortConfig, sortable]);

  if (!rows.length) {
    return renderEmptyState(emptyState, emptyMessage);
  }

  const handleSort = column => {
    if (!sortable || column.sortable === false) {
      return;
    }

    setSortConfig(current => ({
      key: column.key,
      direction:
        current.key === column.key && current.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const containerClassName = [
    'data-display-table__container',
    className,
  ].filter(Boolean).join(' ');

  const tableClassNames = [
    'data-display-table',
    density === 'compact' ? 'data-display-table--compact' : '',
    stickyHeader ? 'data-display-table--sticky' : '',
    tableClassName,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName} style={containerStyle(maxHeight)}>
      <table aria-labelledby={ariaLabelledby} className={tableClassNames} style={getTableStyle(minWidth)}>
        {caption && <caption className={captionMode === 'visually-hidden' ? 'av-visually-hidden' : undefined}>{caption}</caption>}
        {showHeader && (
          <thead>
            <tr>
              {columns.map(column => {
                const isSortable = sortable && column.sortable !== false;
                const isActive = sortConfig.key === column.key;
                const headerStyle = getHeaderStyle({
                  ...column,
                  sticky: stickyHeader,
                });

                return (
                  <th
                    aria-sort={
                      isActive
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    key={column.key}
                    scope="col"
                    style={headerStyle}
                  >
                    {isSortable ? (
                      <button
                        aria-label={`Sort by ${column.label}`}
                        className="data-display-table__sort"
                        onClick={() => handleSort(column)}
                        type="button"
                      >
                        {column.label}
                        {isActive && (
                          <span aria-hidden="true">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        <tbody>
          {sortedRows.map((row, rowIndex) => {
            const activatable = Boolean(onRowActivate);

            return (
              <tr
                data-activatable={activatable ? 'true' : undefined}
                key={resolveRowKey(rowKey, row, rowIndex)}
                onClick={activatable ? () => onRowActivate(row) : undefined}
                onKeyDown={
                  activatable
                    ? event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowActivate(row);
                        }
                      }
                    : undefined
                }
                tabIndex={activatable ? 0 : undefined}
              >
                {columns.map(column => {
                  const value = getColumnValue(column, row);

                  return (
                    <td key={column.key} style={getCellStyle(column)}>
                      {column.render
                        ? column.render(value, row)
                        : formatCellValue(value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
